import { useCallback } from 'react';

import { PlanningEntityIdentifier } from 'generated';
import { UseMutateFunction } from 'react-query';

import { showSnackbar } from 'components/shared';

import { CommandResult, ConnectSchedulePayload } from 'RestAPIs/activities';
import { EditAppointmentType } from 'RestAPIs/activities/types';

import { PlanningEntityMap } from '../types';

type UseConnectScheduleActionProps = {
  connectSchedule: UseMutateFunction<
    CommandResult,
    unknown,
    ConnectSchedulePayload,
    unknown
  >;
  defaultScheduleStart: string;
  onError: () => void;
  planningEntities: PlanningEntityMap | null;
};

type ConnectScheduleTarget = string | PlanningEntityIdentifier | undefined;

function resolvePlanningEntityIdentifier(
  target: ConnectScheduleTarget,
  planningEntities: PlanningEntityMap | null
) {
  if (!target) {
    return undefined;
  }

  if (typeof target !== 'string') {
    return target;
  }

  return planningEntities?.[target]?.planningEntityIdentifier;
}

export default function useConnectScheduleAction({
  connectSchedule,
  defaultScheduleStart,
  onError,
  planningEntities
}: UseConnectScheduleActionProps) {
  return useCallback(
    (
      planningEntityTarget: ConnectScheduleTarget,
      scheduleStart?: string,
      editAppointmentType?: EditAppointmentType
    ) => {
      const planningEntityIdentifier = resolvePlanningEntityIdentifier(
        planningEntityTarget,
        planningEntities
      );

      if (!planningEntityIdentifier) {
        return;
      }

      const payload: ConnectSchedulePayload = {
        planningEntityIdentifier,
        scheduleStart: scheduleStart ?? defaultScheduleStart,
        editAppointmentType: editAppointmentType ?? 'ONLY_THIS',
        suppressWarnings: false
      };

      connectSchedule(payload, {
        onSuccess: (response: any) => {
          if (!response.data.success) {
            showSnackbar({
              severity: 'warning',
              message:
                response.data.notifications?.[0]?.text ?? 'Something went wrong'
            });
          }
        },
        onError
      });
    },
    [connectSchedule, defaultScheduleStart, onError, planningEntities]
  );
}
